import {
  html,
  attr,
  Value,
  When,
  Fragment,
  prop,
  Async,
  Signal,
  computedOf,
} from '@tempots/dom'
import { Anchor } from '@tempots/ui'
import { Icon } from '@tempots/beatui'
import { PageLayout } from '@/components/layout'
import { LoadingState } from '@/components/ui/loading-state'
import { ErrorState } from '@/components/ui/error-state'
import {
  createProjectJobs,
  createProjectPersistentFiles,
} from '@/services/gateway'
import type { PersistentFilesService } from '@/services/persistent-files.service'
import type { JobsService } from '@/services/jobs.service'
import type { JobResponse, JobStatus } from '@/services/types'
import { Workspace } from '@/state/workspace'
import {
  formatDate,
  capitalize,
  formatCredits,
  safeJsonStringify,
} from '@/utils'

export interface JobDetailPageOptions {
  projectId: Signal<string>
  id: Signal<string>
}

export function JobDetailPage({ projectId, id }: JobDetailPageOptions) {
  projectId.on(Workspace.changeProject)

  // Create project-scoped services (memoized)
  let filesServiceInstance: PersistentFilesService | null = null
  let jobsServiceInstance: JobsService | null = null
  let lastProjectId = ''

  const getFilesService = (): PersistentFilesService | null => {
    const pid = projectId.value
    if (!pid) {
      return null
    }
    if (pid !== lastProjectId) {
      filesServiceInstance = createProjectPersistentFiles(pid)
      jobsServiceInstance = createProjectJobs(pid)
      lastProjectId = pid
    }
    return filesServiceInstance
  }

  const getJobsService = (): JobsService | null => {
    const pid = projectId.value
    if (!pid) return null
    if (pid !== lastProjectId) {
      filesServiceInstance = createProjectPersistentFiles(pid)
      jobsServiceInstance = createProjectJobs(pid)
      lastProjectId = pid
    }
    return jobsServiceInstance
  }

  // Helper to get content URL (for ephemeral output files)
  const getDownloadUrl = (hash: string): string => {
    const filesService = getFilesService()
    if (!filesService) {
      return '#'
    }
    return filesService.getContentUrl(hash)
  }

  const jobId = Value.toSignal(id)
  const job = prop<JobResponse | null>(null)

  // Polling interval for running jobs
  let pollInterval: ReturnType<typeof setInterval> | null = null

  const statusConfig: Record<
    JobStatus,
    { icon: string; bg: string; border: string; text: string; iconBg: string }
  > = {
    pending: {
      icon: 'mdi:clock-outline',
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-900',
      iconBg: 'bg-yellow-100 text-yellow-600',
    },
    processing: {
      icon: 'mdi:loading',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-900',
      iconBg: 'bg-blue-100 text-blue-600',
    },
    completed: {
      icon: 'mdi:check',
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-900',
      iconBg: 'bg-green-100 text-green-600',
    },
    failed: {
      icon: 'mdi:alert-circle',
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-900',
      iconBg: 'bg-red-100 text-red-600',
    },
  }

  // Load job data
  const loadJob = async () => {
    const id = jobId.get()
    if (!id) {
      throw new Error('No job ID provided')
    }

    const jobsService = getJobsService()
    if (!jobsService) {
      throw new Error('No project selected')
    }

    const result = await jobsService.get(id)
    job.set(result)

    // Start polling if job is pending or processing
    if (
      (result.status === 'pending' || result.status === 'processing') &&
      !pollInterval
    ) {
      pollInterval = setInterval(() => {
        loadJob()
      }, 5000)
    }

    // Stop polling if job is completed or failed
    if (
      (result.status === 'completed' || result.status === 'failed') &&
      pollInterval
    ) {
      clearInterval(pollInterval)
      pollInterval = null
    }

    return result
  }

  // Initial load promise
  const initialLoad = loadJob()

  return PageLayout({
    header: html.div(
      attr.class('flex items-center gap-3'),
      Anchor(
        { href: '/jobs' },
        attr.class('text-gray-400 hover:text-gray-600'),
        Icon({ icon: 'mdi:chevron-left' }, attr.class('w-5 h-5'))
      ),
      html.span(attr.class('font-mono'), jobId)
    ),
    content: Async(initialLoad, {
      pending: () => LoadingState({ message: 'Loading job...', size: 'lg' }),
      error: (err) =>
        ErrorState({
          error: prop<string | null>(
            err instanceof Error ? err.message : 'Failed to load job'
          ),
        }),
      then: () =>
        Fragment(
          // Status Header
          html.div(
            attr.class(
              job.map((j) => {
                const config = statusConfig[j?.status ?? 'pending']
                return `${config.bg} border ${config.border} rounded-xl p-6`
              })
            ),
            html.div(
              attr.class('flex items-center justify-between'),
              html.div(
                attr.class('flex items-center gap-4'),
                html.div(
                  attr.class(
                    job.map((j) => {
                      const config = statusConfig[j?.status ?? 'pending']
                      return `w-12 h-12 rounded-full flex items-center justify-center ${config.iconBg}`
                    })
                  ),
                  Icon(
                    {
                      icon: job.map((j) => {
                        const config = statusConfig[j?.status ?? 'pending']
                        return config.icon
                      }),
                    },
                    attr.class(
                      job.map((j) =>
                        j?.status === 'processing'
                          ? 'w-6 h-6 animate-spin'
                          : 'w-6 h-6'
                      )
                    )
                  )
                ),
                html.div(
                  html.h2(
                    attr.class(
                      job.map((j) => {
                        const config = statusConfig[j?.status ?? 'pending']
                        return `text-lg font-semibold ${config.text}`
                      })
                    ),
                    job.map((j) => capitalize(j?.status ?? 'pending'))
                  ),
                  html.p(
                    attr.class(
                      job.map((j) => {
                        const config = statusConfig[j?.status ?? 'pending']
                        return `text-sm ${config.text.replace('900', '700')}`
                      })
                    ),
                    job.map((j) => {
                      switch (j?.status) {
                        case 'completed':
                          return 'Job finished successfully'
                        case 'failed':
                          return 'Job failed with an error'
                        case 'processing':
                          return 'Job is currently processing...'
                        default:
                          return 'Job is waiting to be processed'
                      }
                    })
                  )
                )
              ),
              html.span(
                attr.class(
                  job.map((j) => {
                    const config = statusConfig[j?.status ?? 'pending']
                    return `text-sm ${config.text.replace('900', '600')}`
                  })
                ),
                job.map((j) => formatDate(j?.createdAt ?? ''))
              )
            )
          ),
          // Job Overview
          html.div(
            attr.class('bg-white rounded-xl border border-gray-200 p-6'),
            html.h3(
              attr.class('text-base font-semibold text-gray-900 mb-4'),
              'Overview'
            ),
            html.dl(
              attr.class(
                'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
              ),
              OverviewItem(
                'Action',
                html.span(
                  Anchor(
                    {
                      href: computedOf(
                        job,
                        projectId
                      )(
                        (job, pid) =>
                          Workspace.projectUrl(
                            `/actions/${job?.actionName ?? ''}`,
                            pid
                          ) ?? '#'
                      ),
                    },
                    attr.class(
                      'text-brand-600 hover:text-brand-700 font-medium'
                    ),
                    job.map((j) => j?.actionName ?? '')
                  )
                )
              ),
              OverviewItem(
                'Created',
                job.map((j) => formatDate(j?.createdAt ?? ''))
              ),
              When(
                job.map(
                  (j) => j?.completedAt !== undefined && j?.completedAt !== null
                ),
                () =>
                  OverviewItem(
                    'Completed',
                    job.map((j) => formatDate(j?.completedAt ?? ''))
                  )
              ),
              OverviewItem(
                'Credits',
                job.map((j) =>
                  j?.creditsCharged
                    ? formatCredits(j.creditsCharged)
                    : j?.creditsReserved
                      ? `${formatCredits(j.creditsReserved, { includeSuffix: false })} (reserved)`
                      : '-'
                )
              )
            )
          ),
          // Input
          html.div(
            attr.class('bg-white rounded-xl border border-gray-200 p-6'),
            html.h3(
              attr.class('text-base font-semibold text-gray-900 mb-4'),
              'Input'
            ),
            html.div(
              attr.class('mb-4'),
              html.h4(
                attr.class('text-sm font-medium text-gray-700 mb-2'),
                'Files'
              ),
              html.div(
                attr.class('flex flex-wrap gap-2'),
                job.map(
                  (j) =>
                    j?.fileHashes.map((hash, i) =>
                      html.span(
                        attr.class(
                          'px-3 py-1.5 bg-gray-100 rounded-lg text-sm font-mono text-gray-700'
                        ),
                        `File ${i + 1}: ${hash.substring(0, 8)}...`
                      )
                    ) ?? []
                )
              )
            ),
            When(
              job.map(
                (j) => j?.parameters !== undefined && j?.parameters !== null
              ),
              () =>
                html.div(
                  html.h4(
                    attr.class('text-sm font-medium text-gray-700 mb-2'),
                    'Parameters'
                  ),
                  html.div(
                    attr.class('bg-gray-900 rounded-lg p-4 overflow-x-auto'),
                    html.pre(
                      attr.class('text-sm text-gray-100 font-mono'),
                      job.map((j) => safeJsonStringify(j?.parameters ?? {}))
                    )
                  )
                )
            )
          ),
          // Output (only for completed jobs)
          When(
            job.map(
              (j) =>
                j?.status === 'completed' &&
                j?.outputFileHash !== undefined &&
                j?.outputFileHash !== null
            ),
            () =>
              html.div(
                attr.class('bg-white rounded-xl border border-gray-200 p-6'),
                html.h3(
                  attr.class('text-base font-semibold text-gray-900 mb-4'),
                  'Output'
                ),
                html.div(
                  attr.class(
                    'flex items-center justify-between p-3 bg-gray-50 rounded-lg'
                  ),
                  html.div(
                    attr.class('flex items-center gap-3'),
                    html.div(
                      attr.class(
                        'w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center'
                      ),
                      Icon(
                        { icon: 'mdi:file-check' },
                        attr.class('w-5 h-5 text-purple-600')
                      )
                    ),
                    html.div(
                      html.p(
                        attr.class('text-sm font-medium text-gray-900'),
                        'Output File'
                      ),
                      html.p(
                        attr.class('text-xs text-gray-500 font-mono'),
                        job.map(
                          (j) =>
                            `${j?.outputFileHash?.substring(0, 16) ?? ''}...`
                        )
                      )
                    )
                  ),
                  html.div(
                    attr.class('flex gap-2'),
                    Anchor(
                      {
                        href: job.map((j) =>
                          j?.outputFileHash
                            ? getDownloadUrl(j.outputFileHash)
                            : '#'
                        ),
                      },
                      attr.class(
                        'inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50'
                      ),
                      Icon(
                        { icon: 'mdi:download' },
                        attr.class('w-4 h-4 mr-2')
                      ),
                      'Download'
                    )
                  )
                )
              )
          ),
          // Error (only for failed jobs)
          When(
            job.map(
              (j) =>
                j?.status === 'failed' &&
                j?.error !== undefined &&
                j?.error !== null &&
                j?.error !== ''
            ),
            () =>
              html.div(
                attr.class('bg-white rounded-xl border border-gray-200 p-6'),
                html.h3(
                  attr.class('text-base font-semibold text-gray-900 mb-4'),
                  'Error'
                ),
                html.div(
                  attr.class('bg-red-50 border border-red-200 rounded-lg p-4'),
                  html.div(
                    attr.class('flex items-start gap-3'),
                    Icon(
                      { icon: 'mdi:alert-circle' },
                      attr.class('w-5 h-5 text-red-500 flex-shrink-0 mt-0.5')
                    ),
                    html.div(
                      html.p(
                        attr.class('text-sm font-medium text-red-800'),
                        'Processing Error'
                      ),
                      html.p(
                        attr.class('text-sm text-red-700 mt-1'),
                        job.map((j) => j?.error ?? '')
                      )
                    )
                  )
                )
              )
          )
        ),
    }),
  })
}

function OverviewItem(
  label: string,
  value: Value<string> | ReturnType<typeof html.span>
) {
  return html.div(
    html.dt(attr.class('text-sm font-medium text-gray-500'), label),
    html.dd(attr.class('mt-1 text-sm text-gray-900'), value)
  )
}
